import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { motion } from "framer-motion";
import { X, Check } from "lucide-react";
import { TAILWIND_COLORS, isValidHex } from "../../config/colors";

export interface ColorPickerProps {
  value: string | undefined;
  onChange: (value: string | undefined, opacity?: number) => void;
  opacity?: number;
  onOpacityChange?: (opacity: number) => void;
  portalRoot?: HTMLElement | null;
  disabled?: boolean;
  className?: string;
  showHexInput?: boolean;
  showOpacitySlider?: boolean;
  label?: string;
}

const ColorPicker: React.FC<ColorPickerProps> = ({
  value,
  onChange,
  opacity = 1,
  onOpacityChange,
  portalRoot,
  disabled = false,
  className = "",
  showHexInput = true,
  showOpacitySlider = true,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [hexInput, setHexInput] = useState(value || "");
  const [localOpacity, setLocalOpacity] = useState(opacity);
  const [hexError, setHexError] = useState(false);
  
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const hexInputRef = useRef<HTMLInputElement>(null);

  // Update hex input when value changes
  useEffect(() => {
    setHexInput(value || "");
  }, [value]);

  // Update local opacity when prop changes
  useEffect(() => {
    setLocalOpacity(opacity);
  }, [opacity]);

  // Handle opening the dropdown
  const handleOpen = () => {
    if (disabled) return;
    setIsOpen(true);
  };

  // Handle closing the dropdown
  const handleClose = () => {
    setIsOpen(false);
  };

  // Handle color selection
  const handleSelectColor = (colorValue: string | undefined) => {
    // Only pass opacity if onOpacityChange is provided (meaning the parent component supports it)
    if (onOpacityChange) {
      onChange(colorValue, localOpacity);
    } else {
      onChange(colorValue);
    }
    
    if (colorValue) {
      setHexInput(colorValue);
      setHexError(false);
    }
  };

  // Handle hex input change
  const handleHexChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setHexInput(newValue);
    
    // Validate hex format
    if (newValue === "" || newValue === "#") {
      setHexError(false);
      return;
    }
    
    // Add # if missing
    let formattedHex = newValue.startsWith("#") ? newValue : `#${newValue}`;
    
    // Validate hex
    if (isValidHex(formattedHex)) {
      setHexError(false);
      // Only pass opacity if onOpacityChange is provided
      if (onOpacityChange) {
        onChange(formattedHex, localOpacity);
      } else {
        onChange(formattedHex);
      }
    } else {
      setHexError(true);
    }
  };

  // Handle opacity change
  const handleOpacityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newOpacity = parseFloat(e.target.value);
    setLocalOpacity(newOpacity);
    
    // If onOpacityChange is provided, call it and update the color with new opacity
    if (onOpacityChange) {
      onOpacityChange(newOpacity);
      if (value) {
        onChange(value, newOpacity);
      }
    }
    // Otherwise, just update the local state without changing the data model
  };

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isOpen &&
        containerRef.current &&
        !containerRef.current.contains(event.target as Node) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        handleClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  // Calculate dropdown position
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });

  useEffect(() => {
    if (isOpen && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    }
  }, [isOpen]);

  // Render the dropdown content
  const renderDropdown = () => {
    if (!isOpen) return null;

    const dropdownContent = (
      <div
        ref={dropdownRef}
        className="absolute z-50 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-md shadow-lg overflow-hidden"
        style={{
          top: dropdownPosition.top,
          left: dropdownPosition.left,
          width: dropdownPosition.width,
          maxHeight: '400px',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {/* Color grid */}
        <div className="grid grid-cols-5 gap-2 p-3">
          {/* Default/None option */}
          <button
            onClick={() => handleSelectColor(undefined)}
            className={`w-8 h-8 rounded border border-dashed border-gray-400 dark:border-gray-500 hover:ring-2 hover:ring-offset-1 dark:hover:ring-offset-gray-800 hover:ring-indigo-500 flex items-center justify-center bg-white dark:bg-gray-800 ${!value ? 'ring-2 ring-offset-1 dark:ring-offset-gray-800 ring-indigo-500' : ''}`}
            title="Default (None)"
          >
            <div className="w-4 h-0.5 bg-gray-400 dark:bg-gray-500 transform rotate-45"></div>
          </button>
          
          {/* Color swatches */}
          {TAILWIND_COLORS.map(colorOption => (
            <button
              key={colorOption.name}
              onClick={() => handleSelectColor(colorOption.value)}
              className={`w-8 h-8 rounded border border-gray-300 dark:border-gray-700 hover:ring-2 hover:ring-offset-1 dark:hover:ring-offset-gray-800 hover:ring-indigo-500 flex items-center justify-center ${value === colorOption.value ? 'ring-2 ring-offset-1 dark:ring-offset-gray-800 ring-indigo-500' : ''}`}
              style={{ backgroundColor: colorOption.value }}
              title={colorOption.name}
            >
              {value === colorOption.value && (
                <Check className="text-white" size={16} strokeWidth={3} />
              )}
            </button>
          ))}
        </div>

        {/* Hex input */}
        {showHexInput && (
          <div className="px-3 pb-3">
            <label htmlFor="hex-input" className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              Hex Color
            </label>
            <div className="relative">
              <input
                ref={hexInputRef}
                id="hex-input"
                type="text"
                value={hexInput}
                onChange={handleHexChange}
                placeholder="#000000"
                className={`w-full pl-2 pr-6 py-1.5 text-sm border ${hexError ? 'border-red-500 dark:border-red-500' : 'border-gray-300 dark:border-gray-600'} rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100`}
              />
              {hexInput && (
                <motion.button
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  onClick={() => {
                    setHexInput("");
                    handleSelectColor(undefined);
                  }}
                  className="absolute right-2 top-2 h-4 w-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  aria-label="Clear hex input"
                >
                  <X size={16} />
                </motion.button>
              )}
            </div>
            {hexError && (
              <p className="text-xs text-red-500 mt-1">
                Please enter a valid hex color (e.g., #FF0000)
              </p>
            )}
          </div>
        )}

        {/* Opacity slider */}
        {showOpacitySlider && (
          <div className="px-3 pb-3">
            <label htmlFor="opacity-slider" className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              Opacity: {Math.round(localOpacity * 100)}%
            </label>
            <input
              id="opacity-slider"
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={localOpacity}
              onChange={handleOpacityChange}
              className="w-full h-2 bg-gray-200 dark:bg-gray-600 rounded-lg appearance-none cursor-pointer"
            />
          </div>
        )}
      </div>
    );

    // Use portal if available, otherwise render in place
    return portalRoot ? createPortal(dropdownContent, portalRoot) : dropdownContent;
  };

  // Get color name or hex value for display
  const getColorDisplay = () => {
    if (!value) return "Default";
    const colorOption = TAILWIND_COLORS.find(c => c.value === value);
    return colorOption ? colorOption.name : value;
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {/* Selected color button */}
      <button
        type="button"
        onClick={handleOpen}
        className={`flex items-center justify-between w-full pl-3 pr-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 dark:focus:border-blue-600 rounded ${
          disabled
            ? "opacity-60 cursor-not-allowed bg-gray-100 dark:bg-gray-800"
            : "focus:outline-none focus:border-blue-500 focus:ring-blue-500/30 dark:focus:ring-blue-500/20 dark:bg-gray-700 dark:focus:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 transition duration-300 ease-in-out"
        }`}
        disabled={disabled}
      >
        <div className="flex items-center">
          <div 
            className="w-6 h-6 rounded mr-2 border border-gray-300 dark:border-gray-600 flex-shrink-0"
            style={{ 
              backgroundColor: value || 'transparent',
              opacity: localOpacity
            }}
          />
          <span className="text-gray-700 dark:text-gray-300 truncate">
            {getColorDisplay()}
            {showOpacitySlider && opacity !== 1 && ` (${Math.round(opacity * 100)}%)`}
          </span>
        </div>
        <svg
          className="w-4 h-4 text-gray-500 dark:text-gray-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {/* Dropdown */}
      {renderDropdown()}
    </div>
  );
};

export default ColorPicker;
